import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-10 w-auto" />
              <span className="font-bold text-xl text-slate-900">CIA Factura℗</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-8">Aviso de Privacidad</h1>
          
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-6">
              <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Identidad del Responsable</h2>
            <p className="text-slate-600 mb-4">
              <strong>CIA Factura℗</strong>, con domicilio en ciafactura.com, es responsable del tratamiento de los datos personales que nos proporcione, los cuales serán protegidos conforme a lo dispuesto por la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y demás normatividad aplicable.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Datos Personales que Recabamos</h2>
            <p className="text-slate-600 mb-4">Para las finalidades señaladas en este aviso de privacidad, podemos recabar las siguientes categorías de datos personales:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Datos de identificación:</strong> Nombre completo, RFC, CURP, razón social</li>
              <li><strong>Datos de contacto:</strong> Correo electrónico, número telefónico, dirección fiscal</li>
              <li><strong>Datos fiscales:</strong> Régimen fiscal, código postal, certificados de sello digital (CSD)</li>
              <li><strong>Datos financieros:</strong> Información de facturación y métodos de pago para suscripciones</li>
              <li><strong>Datos de uso:</strong> Información sobre cómo utiliza nuestros servicios</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Finalidades del Tratamiento</h2>
            <p className="text-slate-600 mb-4">Los datos personales que recabamos serán utilizados para las siguientes finalidades:</p>
            
            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Finalidades Primarias (necesarias):</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Crear y administrar su cuenta de usuario</li>
              <li>Emisión de Comprobantes Fiscales Digitales por Internet (CFDI)</li>
              <li>Timbrado de facturas, notas de crédito y complementos de pago</li>
              <li>Envío de CFDI a sus clientes por correo electrónico o WhatsApp</li>
              <li>Procesamiento de pagos de suscripción</li>
              <li>Atención de soporte técnico y consultas</li>
              <li>Cumplimiento de obligaciones fiscales ante el SAT</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Finalidades Secundarias (opcionales):</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Envío de comunicaciones promocionales y novedades del servicio</li>
              <li>Realización de encuestas de satisfacción</li>
              <li>Mejora de nuestros productos y servicios</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Transferencia de Datos</h2>
            <p className="text-slate-600 mb-4">
              Sus datos personales podrán ser transferidos a:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Proveedores Autorizados de Certificación (PAC):</strong> Para el timbrado de CFDI ante el SAT</li>
              <li><strong>Procesadores de pago:</strong> Para gestionar sus suscripciones (Stripe)</li>
              <li><strong>Autoridades fiscales:</strong> Cuando sea requerido por ley</li>
              <li><strong>Proveedores de servicios de comunicación:</strong> Para el envío de CFDI por WhatsApp (Twilio)</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Derechos ARCO</h2>
            <p className="text-slate-600 mb-4">
              Usted tiene derecho a conocer qué datos personales tenemos, para qué los utilizamos y las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos personales para fines específicos (Oposición).
            </p>
            <p className="text-slate-600 mb-4">
              Para ejercer sus derechos ARCO, puede enviar una solicitud a través de nuestro portal de soporte o al correo electrónico que se indique en la sección de contacto del panel de administración.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Uso de Cookies y Tecnologías de Rastreo</h2>
            <p className="text-slate-600 mb-4">
              Nuestro sitio web utiliza cookies y otras tecnologías de rastreo para mejorar su experiencia de navegación, analizar el tráfico del sitio y personalizar el contenido. Puede configurar su navegador para rechazar las cookies, aunque esto podría afectar la funcionalidad del servicio.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Medidas de Seguridad</h2>
            <p className="text-slate-600 mb-4">
              Implementamos medidas de seguridad técnicas, administrativas y físicas para proteger sus datos personales contra daño, pérdida, alteración, destrucción o uso, acceso o tratamiento no autorizado, incluyendo:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Encriptación de datos en tránsito y en reposo</li>
              <li>Autenticación segura con tokens JWT</li>
              <li>Acceso restringido a datos sensibles</li>
              <li>Monitoreo continuo de seguridad</li>
              <li>Respaldos periódicos de información</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Cambios al Aviso de Privacidad</h2>
            <p className="text-slate-600 mb-4">
              Nos reservamos el derecho de efectuar modificaciones o actualizaciones al presente aviso de privacidad. Cualquier cambio será notificado a través de nuestro sitio web o por correo electrónico.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Contacto</h2>
            <p className="text-slate-600 mb-4">
              Si tiene alguna duda sobre este aviso de privacidad o sobre el tratamiento de sus datos personales, puede contactarnos a través de los medios indicados en el panel de administración de CIA Factura℗.
            </p>

            <div className="mt-12 p-6 bg-slate-50 rounded-lg">
              <p className="text-slate-600 text-sm">
                <strong>CIA Factura℗</strong> - Sistema de Timbrado de CFDI<br />
                Dominio: ciafactura.com<br />
                Este aviso de privacidad cumple con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
